import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useIssueMessages, useCreateIssueMessage } from '../../hooks/useSystemIssueMessages'
import { useMarkIssueRead } from '../../hooks/useSystemIssues'
import {
  isSummaryMissing,
  sameResolution,
  useResolutionSummaries,
  useSaveResolutionSummary,
} from '../../hooks/useResolutionSummaries'
import { errorToast } from '../../../occurrences/utils/error-toast'
import type { SystemIssue } from '../../types/system-issues'
import { IssueMessageItem } from './IssueMessageItem'
import { IssueMessageEditor } from './IssueMessageEditor'
import { IssueThreadHeader } from './IssueThreadHeader'
import {
  MissingSummaryBanner,
  ResolutionSummaryCard,
  ResolutionSummaryEditor,
  anchorSummaries,
} from './IssueResolutionSummary'

interface IssueThreadProps {
  issue: SystemIssue
  onBack: () => void
  /** Controles de status/atribuição/prioridade só para quem faz triagem (dev). */
  canManage: boolean
  /** Painel estreito ("Relatar problema", 28rem): menos respiro. */
  compact?: boolean
}

/**
 * Conversa de um chamado do Atendimento HUB — o mesmo desenho da conversa de
 * Ocorrências: cabeçalho com cards, mensagens em cards com avatar, mudanças
 * como linhas de evento e o editor embaixo.
 */
export function IssueThread({ issue, onBack, canManage, compact = false }: IssueThreadProps) {
  const { user } = useAuth()
  const { data: messages = [], isLoading } = useIssueMessages(issue.id)
  const createMessage = useCreateIssueMessage(issue.id)
  const markRead = useMarkIssueRead()
  // A abertura é a primeira mensagem de usuário (a descrição).
  const openingId = messages.find((m) => m.kind === 'user')?.id

  // Resumo da solução — só dev. Abre ao resolver; se ficar sem, a faixa
  // lembra. Cada resumo vai na conversa após o "Chamado resolvido" dele.
  const { data: summaries = [], isSuccess: summariesLoaded } = useResolutionSummaries(
    issue.id,
    canManage,
  )
  const saveSummary = useSaveResolutionSummary(issue.id)
  const [writingSummary, setWritingSummary] = useState(false)
  const resolved = issue.status === 'resolved'
  const currentSummary = resolved
    ? summaries.find((s) => sameResolution(s.resolved_at, issue.resolved_at))
    : undefined
  const missingSummary = canManage && summariesLoaded && isSummaryMissing(issue, summaries)
  const anchored = useMemo(
    () => anchorSummaries(messages, canManage ? summaries : []),
    [messages, summaries, canManage],
  )
  const summaryCards = (list: typeof summaries) =>
    list.map((s) => (
      <ResolutionSummaryCard
        key={`resumo-${s.id}`}
        summary={s}
        onEdit={s === currentSummary ? () => setWritingSummary(true) : undefined}
      />
    ))

  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const lastIssueIdRef = useRef<number | null>(null)
  const pendingScrollRef = useRef(false)
  useEffect(() => {
    if (lastIssueIdRef.current !== issue.id) {
      lastIssueIdRef.current = issue.id
      pendingScrollRef.current = true
      // Abrir a thread já conta como "li" — zera o badge de não lidas dela
      // (na lista do reporter e/ou na fila de triagem, dependendo de quem
      // está olhando).
      markRead.mutate(issue.id)
      // Outro chamado na mesma tela: o painel do resumo não passa para ele.
      setWritingSummary(false)
    }
  }, [issue.id])
  useEffect(() => {
    if (!pendingScrollRef.current) return
    if (isLoading) return
    const container = messagesContainerRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
    pendingScrollRef.current = false
  }, [messages, isLoading])

  const header = (
    <IssueThreadHeader
      issue={issue}
      onBack={onBack}
      canManage={canManage}
      compact={compact}
      onResolved={() => setWritingSummary(true)}
    />
  )

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      {/* Gaveta estreita: o cabeçalho (cards em 3 linhas) rola junto com a
          conversa — fixo, sobraria pouco para as mensagens e o "Enviar"
          podia sair da tela. */}
      {!compact && header}

      <div ref={messagesContainerRef} className="min-h-0 flex-1 overflow-y-auto">
        {compact && header}
        <div className={`space-y-4 py-6 ${compact ? 'px-4' : 'px-8'}`}>
          {isLoading && (
            <div className="text-center text-sm text-subtle">Carregando mensagens…</div>
          )}
          {messages.map((m) => (
            <Fragment key={m.id}>
              <IssueMessageItem
                message={m}
                isOpening={m.id === openingId}
                isOwn={user != null && m.author_id === user.id}
              />
              {summaryCards(anchored.after.get(m.id) ?? [])}
            </Fragment>
          ))}
          {summaryCards(anchored.trailing)}
        </div>
      </div>

      {canManage && resolved && writingSummary ? (
        <ResolutionSummaryEditor
          initial={currentSummary?.summary ?? ''}
          saving={saveSummary.isLoading}
          compact={compact}
          onSave={(text) =>
            saveSummary.mutate(text, {
              onSuccess: () => setWritingSummary(false),
              onError: errorToast('Não foi possível salvar o resumo da solução.'),
            })
          }
        />
      ) : (
        missingSummary && (
          <MissingSummaryBanner compact={compact} onWrite={() => setWritingSummary(true)} />
        )
      )}

      <IssueMessageEditor
        isSending={createMessage.isLoading}
        compact={compact}
        onSend={(content, attachments) => {
          createMessage.mutate(
            { content, attachments },
            {
              onSuccess: () => {
                pendingScrollRef.current = true
              },
            },
          )
        }}
      />
    </div>
  )
}
